import React, { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Box, SelectChangeEvent, Typography } from '@mui/material';
import { ChartFilters } from './components/ChartFilters';
import { ChartContent } from './components/ChartContent';
import {
  useChartData,
  Entity,
  DataSource,
  ShowAs,
  GroupBy,
  DateRange,
  StatusFilter
} from './hooks/useChartData';

const queryClient = new QueryClient();

interface ChartPageContentProps {
  entities: Entity[];
}

const ChartPageContent = ({ entities = [] }: ChartPageContentProps) => {
  const {
    state: { dataSource, showAs, groupBy, dateRange, selectedEntities, selectedStatuses },
    dispatch,
    chartData,
    isPieChart,
    getShowAsOptions,
    getGroupByOptions,
    getDateRangeOptions,
    availableStatuses,
    // Download-related properties and functions
    downloadProgress,
    downloadPastYearData,
    hasDownloadedData
  } = useChartData(entities);

  const [showLoader, setShowLoader] = useState(false);
  const isLoading =
    (downloadProgress.status === 'downloading' || downloadProgress.status === 'processing') &&
    !hasDownloadedData;

  // Debounce the loader to prevent flickering on fast data loads (e.g., from local storage).
  // This ensures the loading indicator only appears for genuine, longer-lasting data fetches.
  useEffect(() => {
    let timer: NodeJS.Timeout;
    // If the component is in a loading state, set a timer.
    if (isLoading) {
      // Only show the loader if the loading state persists for more than 200ms.
      timer = setTimeout(() => {
        setShowLoader(true);
      }, 200);
    } else setShowLoader(false);
    return () => clearTimeout(timer);
  }, [isLoading]);

  const handleDataSourceChange = (event: SelectChangeEvent<DataSource>) => {
    dispatch({ type: 'SET_DATA_SOURCE', payload: event.target.value as DataSource });
  };

  const handleShowAsChange = (event: SelectChangeEvent<ShowAs>) => {
    dispatch({ type: 'SET_SHOW_AS', payload: event.target.value as ShowAs });
  };

  const handleGroupByChange = (event: SelectChangeEvent<GroupBy>) => {
    dispatch({ type: 'SET_GROUP_BY', payload: event.target.value as GroupBy });
  };

  const handleDateRangeChange = (event: SelectChangeEvent<DateRange>) => {
    dispatch({ type: 'SET_DATE_RANGE', payload: event.target.value as DateRange });
  };

  const handleEntitiesChange = (_: React.SyntheticEvent, newValue: Entity[]) => {
    dispatch({ type: 'SET_ENTITIES', payload: newValue });
  };

  const handleStatusesChange = (_: React.SyntheticEvent, newValue: StatusFilter[]) => {
    dispatch({ type: 'SET_STATUSES', payload: newValue });
  };

  return (
    <Box sx={{ p: 3, position: 'relative' }}>
      <Typography
        component="h1"
        sx={{
          fontWeight: 'bold',
          fontSize: '38px',
          color: '#666666',
          mb: 4,
          fontFamily: 'inherit'
        }}
      >
        Visual Reports
      </Typography>
      <ChartFilters
        dataSource={dataSource}
        showAs={showAs}
        groupBy={groupBy}
        dateRange={dateRange}
        selectedEntities={selectedEntities}
        selectedStatuses={selectedStatuses}
        entities={entities}
        availableStatuses={availableStatuses}
        hasData={hasDownloadedData}
        onDataSourceChange={handleDataSourceChange}
        onShowAsChange={handleShowAsChange}
        onGroupByChange={handleGroupByChange}
        onDateRangeChange={handleDateRangeChange}
        onEntitiesChange={handleEntitiesChange}
        onStatusesChange={handleStatusesChange}
        onPresetSelect={(preset) => {
          dispatch({ type: 'SET_PRESET_FILTERS', payload: preset });
        }}
        getShowAsOptions={getShowAsOptions}
        getGroupByOptions={getGroupByOptions}
        getDateRangeOptions={getDateRangeOptions}
      />

      <ChartContent
        dataSource={dataSource}
        showAs={showAs}
        groupBy={groupBy}
        chartData={chartData}
        isLoading={showLoader}
        isPieChart={isPieChart}
        error={downloadProgress.error ? new Error(downloadProgress.error) : null}
        chartTitle={chartData?.chartTitle}
        onRetry={
          ['applications', 'jobs'].includes(dataSource)
            ? () => downloadPastYearData(selectedEntities)
            : undefined
        }
        downloadProgress={downloadProgress}
      />
    </Box>
  );
};

interface ChartPageProps {
  entities: Entity[];
}

const ChartPage = ({ entities }: ChartPageProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ChartPageContent entities={entities} />
    </QueryClientProvider>
  );
};

export default ChartPage;
